/** @format */

/**
 * 通用 OTEL Span 树拓扑：按 ParentSpanId 父子关系布局（非 Langfuse / LangGraph 场景）。
 */

import type { TraceAnalysisDetailSpan } from '../../typescript'
import { LANGGRAPH_ROOT_KEY } from '../langgraphSpanTree'
import { buildSpanTree, walkSpanTree } from '../traceSpanShared'
import type { SpanTreeNode } from '../traceSpanShared'
import type { TopologyLayoutDirection, TopologyLayoutOptions, TopologySpanNode } from './types'

const HORIZONTAL_LAYER_GAP = 100
const HORIZONTAL_SIBLING_GAP = 48

interface SpanTreeShapeMetrics {
  maxDepth: number
  maxBreadth: number
}

function decideSpanTreeLayoutDirection(metrics: SpanTreeShapeMetrics): TopologyLayoutDirection {
  return metrics.maxBreadth > metrics.maxDepth ? 'horizontal' : 'vertical'
}

function measureSpanTreeShape(nodes: SpanTreeNode[]): SpanTreeShapeMetrics {
  if (!nodes.length) {
    return { maxDepth: 0, maxBreadth: 0 }
  }

  let maxDepth = 1
  let maxBreadth = nodes.length

  const walk = (list: SpanTreeNode[], depth: number) => {
    maxDepth = Math.max(maxDepth, depth)
    maxBreadth = Math.max(maxBreadth, list.length)
    for (const node of list) {
      if (node.children.length) {
        walk(node.children, depth + 1)
      }
    }
  }

  walk(nodes, 1)
  return { maxDepth, maxBreadth }
}

function layoutSpanTreeVertical(
  nodes: SpanTreeNode[],
  nodeWidth: number,
  nodeHeight: number,
  gapX: number,
  gapY: number,
  depth = 0,
  startX = 0
): number {
  let cursor = startX

  for (const node of nodes) {
    if (!node.children.length) {
      node.x = cursor
      node.y = depth * (nodeHeight + gapY)
      cursor += nodeWidth + gapX
    } else {
      const endX = layoutSpanTreeVertical(
        node.children,
        nodeWidth,
        nodeHeight,
        gapX,
        gapY,
        depth + 1,
        cursor
      )
      const childrenCenter = (cursor + endX - gapX) / 2
      node.x = Math.max(cursor, childrenCenter - nodeWidth / 2)
      node.y = depth * (nodeHeight + gapY)
      cursor = endX
    }
  }

  return cursor
}

function layoutSpanTreeHorizontal(
  nodes: SpanTreeNode[],
  nodeWidth: number,
  nodeHeight: number,
  depth = 0,
  startY = 0
): number {
  let cursor = startY

  for (const node of nodes) {
    if (!node.children.length) {
      node.x = depth * (nodeWidth + HORIZONTAL_LAYER_GAP)
      node.y = cursor
      cursor += nodeHeight + HORIZONTAL_SIBLING_GAP
    } else {
      const endY = layoutSpanTreeHorizontal(node.children, nodeWidth, nodeHeight, depth + 1, cursor)
      const childrenCenter = (cursor + endY - HORIZONTAL_SIBLING_GAP) / 2
      node.x = depth * (nodeWidth + HORIZONTAL_LAYER_GAP)
      node.y = Math.max(cursor, childrenCenter - nodeHeight / 2)
      cursor = endY
    }
  }

  return cursor
}

function layoutSpanTree(
  nodes: SpanTreeNode[],
  direction: TopologyLayoutDirection,
  opts: TopologyLayoutOptions
) {
  const nodeWidth = opts.nodeWidth ?? 168
  const nodeHeight = opts.nodeHeight ?? 60
  const gapX = opts.gapX ?? 28
  const gapY = opts.gapY ?? 40

  if (direction === 'horizontal') {
    layoutSpanTreeHorizontal(nodes, nodeWidth, nodeHeight)
    return
  }
  layoutSpanTreeVertical(nodes, nodeWidth, nodeHeight, gapX, gapY)
}

function pruneSpanTreeForLayout(nodes: SpanTreeNode[], collapsedIds: Set<string>): SpanTreeNode[] {
  return nodes.map(node => ({
    span: node.span,
    children: collapsedIds.has(node.span.SpanId)
      ? []
      : pruneSpanTreeForLayout(node.children, collapsedIds)
  }))
}

export function collectVisibleSpanIds(
  childrenMap: Map<string, TraceAnalysisDetailSpan[]>,
  collapsedIds: Set<string>
): Set<string> {
  const visible = new Set<string>()

  const walk = (parentKey: string) => {
    if (parentKey !== LANGGRAPH_ROOT_KEY && collapsedIds.has(parentKey)) {
      return
    }
    const children = childrenMap.get(parentKey) ?? []
    for (const child of children) {
      visible.add(child.SpanId)
      walk(child.SpanId)
    }
  }

  walk(LANGGRAPH_ROOT_KEY)
  return visible
}

export interface SpanTreeTopology {
  nodes: TopologySpanNode[]
  layoutDirection: TopologyLayoutDirection
  visibleSpanIds: Set<string>
}

export function buildSpanTreeTopology(
  childrenMap: Map<string, TraceAnalysisDetailSpan[]>,
  collapsedSpanIds: Set<string>,
  options: TopologyLayoutOptions = {}
): SpanTreeTopology {
  const visibleSpanIds = collectVisibleSpanIds(childrenMap, collapsedSpanIds)
  const roots = buildSpanTree(childrenMap, LANGGRAPH_ROOT_KEY, false)
  const prunedRoots = pruneSpanTreeForLayout(roots, collapsedSpanIds)
  const layoutDirection = decideSpanTreeLayoutDirection(measureSpanTreeShape(prunedRoots))
  layoutSpanTree(prunedRoots, layoutDirection, options)

  const nodes: TopologySpanNode[] = []
  walkSpanTree(prunedRoots, node => {
    if (node.x != null && node.y != null) {
      nodes.push({ span: node.span, x: node.x, y: node.y })
    }
  })

  return { nodes, layoutDirection, visibleSpanIds }
}
